// Popularity Index — composite 0–100 score combining chart rank, average
// rating, and rating count into a single normalized market-quality signal.
//
// Scoring breakdown (max 100 pts):
//   Chart component   (max 40 pts): linear decay rank #1→40pts, #100→~0.4pts
//   Rating average    (max 30 pts): proportional to the 0–5★ scale
//   Rating count      (max 30 pts): log-scaled relative to the max across rows
//
// Honesty note: this is a heuristic index for developer prioritization. It is
// not a download count, revenue estimate, or authoritative ranking.

import { bestChartRank } from './chartRank.js';

/**
 * Compute a 0–100 popularity score for a single row.
 *
 * @param {import('./normalizeApp.js').RegionRow} row
 * @param {number} maxRatingCount - Maximum userRatingCount across all available rows.
 * @returns {number|null} Rounded integer 0–100, or null if row.available === false.
 */
export function computePopularityScore(row, maxRatingCount) {
  if (!row || row.available === false) return null;

  // --- Chart component (max 40 pts) ---
  const chartRank = bestChartRank(row);
  let chartPts = 0;
  if (chartRank !== null && chartRank >= 1 && chartRank <= 100) {
    chartPts = 40 * (1 - (chartRank - 1) / 99);
  }

  // --- Rating average component (max 30 pts) ---
  let ratingPts = 0;
  if (row.averageUserRating !== null && row.averageUserRating !== undefined) {
    ratingPts = (row.averageUserRating / 5) * 30;
  }

  // --- Rating count component (max 30 pts) ---
  let countPts = 0;
  const ratingCount = row.userRatingCount ?? null;
  if (ratingCount !== null && maxRatingCount > 0) {
    const logCount = Math.log10(ratingCount + 1);
    const logMax = Math.log10(maxRatingCount + 1);
    countPts = logMax > 0 ? (logCount / logMax) * 30 : 0;
  }

  const total = chartPts + ratingPts + countPts;
  return Math.min(100, Math.max(0, Math.round(total)));
}

/**
 * Add `popularityScore` (0–100 or null) and `popularityRank` (1-indexed,
 * null for unavailable) to each row. Rows are not reordered.
 * Ties share the same rank (standard competition ranking: 1,2,2,4).
 *
 * @param {import('./normalizeApp.js').RegionRow[]} rows
 * @returns {object[]} New array with popularityScore/popularityRank added.
 */
export function rankByPopularity(rows) {
  if (!Array.isArray(rows)) return [];

  // Find max rating count across all available rows.
  const availableRows = rows.filter((r) => r && r.available !== false && !r.error);
  const maxRatingCount = availableRows.reduce((max, r) => {
    const c = r.userRatingCount ?? 0;
    return c > max ? c : max;
  }, 0);

  // Compute scores.
  const scored = rows.map((row) => ({
    ...row,
    popularityScore: computePopularityScore(row, maxRatingCount),
    popularityRank: null,
  }));

  // Assign ranks among available rows only (standard competition ranking).
  const withScores = scored
    .filter((r) => r.popularityScore !== null)
    .sort((a, b) => b.popularityScore - a.popularityScore);

  let rank = 1;
  for (let i = 0; i < withScores.length; i++) {
    if (i > 0 && withScores[i].popularityScore !== withScores[i - 1].popularityScore) {
      rank = i + 1;
    }
    withScores[i].popularityRank = rank;
  }

  // Build a lookup map from countryCode → { popularityScore, popularityRank }.
  const rankMap = new Map(
    withScores.map((r) => [r.countryCode, { popularityScore: r.popularityScore, popularityRank: r.popularityRank }]),
  );

  return scored.map((r) => {
    const info = rankMap.get(r.countryCode);
    if (info) {
      r.popularityScore = info.popularityScore;
      r.popularityRank = info.popularityRank;
    }
    return r;
  });
}
